"use client";
import React, { useRef, Dispatch, SetStateAction } from "react";
import Image from "next/image";
import {
  PortfolioApiOption,
  PortfolioApiQuestion,
} from "@/type/servicesPageTypes";
import Icon from "@/components/icons/Icons";
import CustomDropdown from "@/components/ui/CustomDropdown";
import { handleField } from "@/utils/allValidations";
import { formatIndianNumber } from "@/utils/commonFunctions";

interface QuestionItemProps {
  question: PortfolioApiQuestion;
  answers: {
    [key: number]: {
      question_id: number;
      option_id: number;
      answer: string;
    };
  };

  errors: Record<number, string>;
  setErrors: Dispatch<SetStateAction<Record<string, string>>>;

  openDropdown: number | null;
  setOpenDropdown: (
    val: number | null | ((prev: number | null) => number | null),
  ) => void;

  handleSelect: (questionId: number, optionId: number, answer: string) => void;

  questionRefCallback: (el: HTMLDivElement | null) => void;
  isActive: boolean;
  onClick: () => void;
}

export default function QuestionItem({
  question,
  answers,
  errors,
  setErrors,
  openDropdown,
  setOpenDropdown,
  handleSelect,
  questionRefCallback,
  isActive,
  onClick,
}: QuestionItemProps) {
  const isDropdownOpen = openDropdown === question.id;
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const hasSelectedOption = answers[question.id] !== undefined;

  return (
    <div
      ref={questionRefCallback}
      onClick={onClick}
      className="space-y-3.5 group"
    >
      <div
        className={[
          "sm:text-xl font-semibold sm:leading-1.3 text-primaryBlack transition",
          isActive || hasSelectedOption
            ? "opacity-100"
            : "opacity-40 group-hover:opacity-60",
        ].join(" ")}
      >
        {question.question}
      </div>

      {errors[question.id] && question.field_type !== "amount" && (
        <p className="text-xs sm:text-sm leading-1.3! text-red-500">
          Please select an option
        </p>
      )}

      {/* RADIO + RADIO BOX */}
      {(question.field_type === "radio" ||
        question.field_type === "radio box") && (
        <div
          className={[
            "grid grid-cols-1 gap-4 sm:grid-cols-2 transition",
            isActive || hasSelectedOption
              ? "opacity-100"
              : "opacity-40 group-hover:opacity-60",
          ].join(" ")}
        >
          {question.options.map((option) => {
            const isSelected = answers[question.id]?.option_id === option.id;
            const isRadioBox = question.field_type === "radio box";

            return (
              <button
                key={option.id}
                type="button"
                onClick={() =>
                  handleSelect(question.id, option.id, option.option)
                }
                className={[
                  "group/option flex items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition",
                  isSelected
                    ? "border-primaryGreen bg-[#EFFFFE]"
                    : "border-[#D9D9D9] sm:hover:border-primaryGreen/70",
                ].join(" ")}
              >
                {/* Radio */}
                <div
                  className={[
                    "relative flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition",
                    "after:absolute after:h-2.5 after:w-2.5 after:rounded-full after:transition",
                    isSelected
                      ? "border-primaryGreen after:bg-primaryGreen"
                      : "border-[#7B7B7B] after:bg-transparent sm:group-hover/option:border-primaryGreen/90",
                  ].join(" ")}
                />

                {/* Only for radio box */}
                {isRadioBox && option.logo && (
                  <div className="relative h-8 w-8 sm:h-10 sm:w-10 shrink-0 overflow-hidden rounded-full bg-[#F4F4F4]">
                    <Image
                      src={option.logo}
                      alt={option.option}
                      fill
                      className="object-contain p-1.5"
                    />
                  </div>
                )}

                {/* Text */}
                <div
                  className={[
                    "text-sm sm:text-base transition",
                    isSelected
                      ? "text-primaryGreen font-medium"
                      : "text-[#555555] sm:group-hover/option:text-primaryGreen/90",
                  ].join(" ")}
                >
                  {option.option}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* SELECT */}
      {question.field_type === "select" &&
        (() => {
          const isDropdownSelected = !!answers[question.id];
          const selectedOption = question.options.find(
            (opt) => opt.id === answers[question.id]?.option_id,
          );

          return (
            <div
              className={[
                "relative w-full transition",
                isActive || hasSelectedOption
                  ? "opacity-100"
                  : "opacity-40 group-hover:opacity-60",
              ].join(" ")}
            >
              <button
                ref={triggerRef}
                type="button"
                className={[
                  "w-full flex justify-between items-center rounded-xl border px-3.5 py-3 text-left transition",
                  isDropdownSelected
                    ? "border-primaryGreen bg-[#EFFFFE]"
                    : "border-[#D9D9D9] sm:hover:border-primaryGreen/70",
                ].join(" ")}
                onClick={() =>
                  setOpenDropdown((prev) =>
                    prev === question.id ? null : question.id,
                  )
                }
              >
                <p
                  className={[
                    "text-sm sm:text-base transition",
                    isDropdownSelected
                      ? "text-primaryGreen font-medium"
                      : "text-[#555555]",
                  ].join(" ")}
                >
                  {selectedOption?.option || "Choose an Option"}
                </p>

                <Icon.dropdownDownArrow
                  className={[
                    "shrink-0 transition",
                    isDropdownOpen && "rotate-180",
                    isDropdownSelected ? "text-primaryGreen" : "text-[#555555]",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                />
              </button>

              <CustomDropdown
                excludeRefs={[triggerRef]}
                isOpen={isDropdownOpen}
                onClose={() => setOpenDropdown(null)}
                className="w-full mt-1 rounded-lg shadow-lg z-1 overflow-hidden"
              >
                <div className="flex flex-col">
                  {question.options.map((opt: PortfolioApiOption) => {
                    const isSelected =
                      answers[question.id]?.option_id === opt.id;

                    return (
                      <button
                        key={opt.id}
                        type="button"
                        className={[
                          "px-4 py-3 text-left text-xs md:text-sm transition",
                          isSelected
                            ? "bg-[#EFFFFE] text-primaryGreen font-medium"
                            : "hover:bg-gray-100 text-[#555555]",
                        ].join(" ")}
                        onClick={() => {
                          handleSelect(question.id, opt.id, opt.option);
                          setOpenDropdown(null);
                        }}
                      >
                        {opt.option}
                      </button>
                    );
                  })}
                </div>
              </CustomDropdown>
            </div>
          );
        })()}

      {question.field_type === "amount" && (
        <div
          className={[
            "transition",
            isActive || hasSelectedOption
              ? "opacity-100"
              : "opacity-40 group-hover:opacity-60",
          ].join(" ")}
        >
          <div
            className={`flex items-center border rounded-xl overflow-hidden ${
              errors[question.id]
                ? "border-red-500"
                : "border-[#D9D9D9] focus-within:border-primaryGreen"
            }`}
          >
            <span className="px-3 py-3 bg-gray-50 border-r text-gray-500">
              ₹
            </span>

            <input
              type="text"
              min={0}
              placeholder="Enter amount"
              inputMode="numeric"
              value={formatIndianNumber(answers[question.id]?.answer) || "0"}
              onChange={(e) => {
                handleField({
                  type: "number",
                  value: e.target.value,
                  field: String(question.id),
                  mode: "change",
                  prevValue: answers[question.id]?.answer ?? "0",
                  setValue: (value) => {
                    handleSelect(question.id, 0, value);
                  },
                  setErrors: setErrors,
                });
              }}
              onBlur={(e) => {
                const minAmount =
                  question.id === 141
                    ? 5000
                    : question.id === 142
                      ? 100000
                      : undefined;
                const maxAmount =
                  question.id === 141
                    ? 1000000000
                    : question.id === 142
                      ? 10000000000
                      : undefined;

                const minLabel =
                  question.id === 141
                    ? "₹5,000"
                    : question.id === 142
                      ? "₹1,00,000"
                      : "";

                handleField({
                  type: "number",
                  value: e.target.value,
                  field: String(question.id),
                  mode: "blur",
                  min: minAmount,
                  max: maxAmount,
                  setErrors: setErrors,
                  message: `Minimum amount is ${minLabel}`,
                });
              }}
              className="flex-1 px-3 py-3 outline-hidden"
            />
          </div>

          {errors[question.id] ? (
            <p className="text-red-500 text-xs mt-1">{errors[question.id]}</p>
          ) : (
            <p className="text-gray-400 text-xs mt-1">
              {question.id === 141
                ? "Minimum: ₹5,000"
                : question.id === 142
                  ? "Minimum: ₹1,00,000"
                  : ""}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
